import {fetchFormForEntity} from '../../api/forms'
import {EntityType} from '../../api/forms/types'
import {queryClient} from '../../utils/react-query'

export type FormStepType = 'class' | 'service' | 'subscription'

type FormStepSession = {
  classId?: string
}

export type FormStepValues = {
  class?: {uid?: string}
  classId?: string
  service?: {uid?: string}
  selectedSessions?: FormStepSession[]
}

export const getFormEntityType = (type: FormStepType, values: FormStepValues): EntityType => {
  if (type === 'subscription') {
    return 'subscription'
  }
  const hasSessions = Boolean(values.selectedSessions && values.selectedSessions.length > 0)
  return values.class || values.classId || hasSessions ? 'class' : 'service'
}

export const getFormEntityIds = (type: FormStepType, values: FormStepValues, subscriptionId?: string): string[] => {
  if (type === 'subscription') {
    return [subscriptionId].filter(Boolean) as string[]
  }
  if (values.selectedSessions && values.selectedSessions.length > 0) {
    const ids = values.selectedSessions.map((s) => s.classId || values.class?.uid || values.classId).filter(Boolean)
    return Array.from(new Set(ids)) as string[]
  }
  if (values.class || values.classId) {
    return [values.class?.uid || values.classId].filter(Boolean) as string[]
  }
  return [values.service?.uid].filter(Boolean) as string[]
}

export const getFormForEntityQueryKey = (entityType: EntityType, entityId?: string) => [
  'formForEntity',
  entityType,
  entityId,
]

// Asks, before leaving the current step, whether any of the entities has a form, so the Form step
// is only opened when there is something to fill in. The answers stay in the query cache, so the
// Form step renders the form at once instead of an empty "Loading..." screen. A failed lookup
// counts as "no form" — the same as the Form step itself does.
export const hasFormForEntities = async (entityType: EntityType, entityIds: string[]): Promise<boolean> => {
  const forms = await Promise.all(
    entityIds.map((entityId) =>
      queryClient
        .fetchQuery(getFormForEntityQueryKey(entityType, entityId), () => fetchFormForEntity(entityType, entityId))
        .catch(() => null),
    ),
  )
  return forms.some(Boolean)
}

// Long enough for the Form step to reuse the form looked up on the previous step instead of
// fetching it a second time on mount.
export const FORM_STALE_TIME = 30_000

export type StepAfterSummary = {kind: 'navigate'; path: string; nextStepPath?: string} | {kind: 'submit'}

// Where the Book button of the booking summary leads: through the Form step only when there is a
// form, then to the payment step or straight to submitting the booking.
export const getStepAfterSummary = (hasForm: boolean, withPaymentStep: boolean): StepAfterSummary => {
  if (withPaymentStep) {
    return hasForm
      ? {kind: 'navigate', path: '../form', nextStepPath: '../set-payment-method'}
      : {kind: 'navigate', path: '../set-payment-method'}
  }
  return hasForm ? {kind: 'navigate', path: '../form', nextStepPath: 'submit'} : {kind: 'submit'}
}

export const getStepAfterSubscriptionProfile = (hasForm: boolean): string =>
  hasForm ? './form' : './set-payment-method'
